// adapters/dayjs-date.adapter.ts
import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import 'dayjs/locale/en';
import 'dayjs/locale/nl'; // Import the Dutch locale
import 'dayjs/locale/fr'; // Import the French locale
import 'dayjs/locale/de'; // Import the German locale

@Injectable()
export class DayjsDateAdapter extends DateAdapter<dayjs.Dayjs> {
	constructor(@Optional() @Inject(MAT_DATE_LOCALE) public dateLocale: string) {
		super();
		this.initializeParser(dateLocale);
	}

	getNumDaysInMonth(date: dayjs.Dayjs): number {
		return date.daysInMonth();
	}

	toIso8601(date: dayjs.Dayjs): string {
		return date.toISOString();
	}

	isDateInstance(obj: any): boolean {
		return dayjs.isDayjs(obj);
	}

	isValid(date: dayjs.Dayjs): boolean {
		return dayjs.isDayjs(date) && date.isValid();
	}

	invalid(): dayjs.Dayjs {
		return dayjs(null);
	}

	private localeData!: {
		firstDayOfWeek: number;
		longMonths: string[];
		shortMonths: string[];
		dates: string[];
		longDaysOfWeek: string[];
		shortDaysOfWeek: string[];
		narrowDaysOfWeek: string[];
	};

	setLocale(locale: string) {
		if (locale === 'du') locale = 'nl';
		super.setLocale(locale);
		dayjs.locale(locale);
		this.updateLocaleData();
	}

	getYear(date: dayjs.Dayjs): number {
		return date.year();
	}

	getMonth(date: dayjs.Dayjs): number {
		return date.month();
	}

	getDate(date: dayjs.Dayjs): number {
		return date.date();
	}

	getDayOfWeek(date: dayjs.Dayjs): number {
		return date.day();
	}

	getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
		return style === 'long' ? this.localeData.longMonths : this.localeData.shortMonths;
	}

	getDateNames(): string[] {
		return this.localeData.dates;
	}

	getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		if (style === 'long') {
			return this.localeData.longDaysOfWeek;
		}
		if (style === 'short') {
			return this.localeData.shortDaysOfWeek;
		}
		return this.localeData.narrowDaysOfWeek;
	}

	getYearName(date: dayjs.Dayjs): string {
		return date.format('YYYY');
	}

	getFirstDayOfWeek(): number {
		return 1;
	}

	createDate(year: number, month: number, date: number): dayjs.Dayjs {
		return dayjs().year(year).month(month).date(date);
	}

	today(): dayjs.Dayjs {
		return dayjs();
	}
	parse(value: any, parseFormat: string | string[]): dayjs.Dayjs | null {
		if (!value) {
			return null;
		}

		// If value is already a Dayjs instance, return it
		if (dayjs.isDayjs(value)) {
			return value;
		}

		if (typeof value === 'string') {
			// Try multiple formats for manual input
			const formatsToTry = [
				'DD/MM/YYYY',
				'DD/MM/YY',
				'DD/M/YYYY',
				'DD/M/YY',
				'D/M/YYYY',
				'D/M/YY',
				'DD-MM-YYYY',
				'DD-MM-YY',
				'DD-M-YYYY',
				'DD-M-YY',
				'D-M-YYYY',
				'D-M-YY',
				'DD.MM.YYYY',
				'DD.MM.YY',
				'DD.M.YYYY',
				'DD.M.YY',
				'D.M.YYYY',
				'D.M.YY',
			];
			// First try the formats that match the display
			for (const format of formatsToTry) {
				const parsed = dayjs(value, format, this.locale, true);
				if (parsed.isValid()) {
					return parsed;
				}
			}

			// If none of the expected formats work, try default parsing
			const defaultParsed = dayjs(value);
			return defaultParsed.isValid() ? defaultParsed : null;
		}

		// If value is not a string, try to parse it directly
		return dayjs(value);
	}

	format(date: dayjs.Dayjs, displayFormat: string): string {
		return date.format(displayFormat);
	}

	addCalendarYears(date: dayjs.Dayjs, years: number): dayjs.Dayjs {
		return date.add(years, 'year');
	}

	addCalendarMonths(date: dayjs.Dayjs, months: number): dayjs.Dayjs {
		return date.add(months, 'month');
	}

	addCalendarDays(date: dayjs.Dayjs, days: number): dayjs.Dayjs {
		return date.add(days, 'day');
	}

	clone(date: dayjs.Dayjs): dayjs.Dayjs {
		return date.clone();
	}

	private updateLocaleData(): void {
		super.setLocale(this.locale);
		const localeData = dayjs().localeData();
		this.localeData = {
			firstDayOfWeek: localeData.firstDayOfWeek(),
			longMonths: localeData.months(),
			shortMonths: localeData.monthsShort(),
			dates: Array.from({ length: 31 }, (_, i) => String(i + 1)),
			longDaysOfWeek: localeData.weekdays(),
			shortDaysOfWeek: localeData.weekdaysShort(),
			narrowDaysOfWeek: localeData.weekdaysMin(),
		};
	}
	private initializeParser(dateLocale: string) {
		dayjs.extend(LocalizedFormat);
		dayjs.extend(customParseFormat);
		dayjs.extend(localeData);

		// Set the locale globally
		if (dateLocale) {
			dayjs.locale(dateLocale);
		}
	}
	getTimestamp(date: dayjs.Dayjs): number {
		return date.valueOf();
	}

	addDays(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'day');
	}

	addWeeks(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'week');
	}

	addMonths(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'month');
	}

	addHours(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'hour');
	}

	addMinutes(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'minute');
	}

	addSeconds(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).add(amount, 'second');
	}

	differenceInDays(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): number {
		return dayjs(dateLeft).diff(dayjs(dateRight), 'day');
	}

	differenceInHours(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): number {
		return dayjs(dateLeft).diff(dayjs(dateRight), 'hour');
	}

	differenceInMinutes(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): number {
		return dayjs(dateLeft).diff(dayjs(dateRight), 'minute');
	}

	differenceInSeconds(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): number {
		return dayjs(dateLeft).diff(dayjs(dateRight), 'second');
	}

	getISOWeek(date: dayjs.Dayjs): number {
		return dayjs(date).isoWeek();
	}

	setDate(date: dayjs.Dayjs, day: number): dayjs.Dayjs {
		return dayjs(date).date(day);
	}

	setMonth(date: dayjs.Dayjs, month: number): dayjs.Dayjs {
		return dayjs(date).month(month);
	}

	setYear(date: dayjs.Dayjs, year: number): dayjs.Dayjs {
		return dayjs(date).year(year);
	}

	subDays(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'day');
	}

	subWeeks(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'week');
	}

	subMonths(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'month');
	}

	subHours(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'hour');
	}

	subMinutes(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'minute');
	}

	subSeconds(date: dayjs.Dayjs, amount: number): dayjs.Dayjs {
		return dayjs(date).subtract(amount, 'second');
	}

	getDay(date: dayjs.Dayjs): number {
		return dayjs(date).day();
	}

	startOfDay(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).startOf('day');
	}

	endOfDay(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).endOf('day');
	}

	startOfMonth(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).startOf('month');
	}

	endOfMonth(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).endOf('month');
	}

	startOfWeek(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).startOf('week');
	}

	endOfWeek(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).endOf('week');
	}

	getISO8601WeekNumber(date: dayjs.Dayjs): number {
		return dayjs(date).isoWeek();
	}

	getDifferenceInDays(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): number {
		return dayjs(dateLeft).diff(dayjs(dateRight), 'day');
	}

	isSameDay(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): boolean {
		return dayjs(dateLeft).isSame(dayjs(dateRight), 'day');
	}

	isSameMonth(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): boolean {
		return dayjs(dateLeft).isSame(dayjs(dateRight), 'month');
	}

	isSameSecond(dateLeft: dayjs.Dayjs, dateRight: dayjs.Dayjs): boolean {
		return dayjs(dateLeft).isSame(dayjs(dateRight), 'second');
	}

	setHours(date: dayjs.Dayjs, hours: number): dayjs.Dayjs {
		return dayjs(date).hour(hours);
	}

	setMinutes(date: dayjs.Dayjs, minutes: number): dayjs.Dayjs {
		return dayjs(date).minute(minutes);
	}

	startOfMinute(date: dayjs.Dayjs): dayjs.Dayjs {
		return dayjs(date).startOf('minute');
	}

	max(dates: dayjs.Dayjs[]): dayjs.Dayjs {
		return dates.reduce((acc, date) => (date > acc ? date : acc));
	}

	getHours(date: dayjs.Dayjs): number {
		return dayjs(date).hour();
	}

	getMinutes(date: dayjs.Dayjs): number {
		return dayjs(date).minute();
	}

	getTimezoneOffset(date: dayjs.Dayjs): number {
		return -dayjs(date).utcOffset();
	}
	/**
	 * Override deserialize to handle Date objects from backend
	 */
	deserialize(value: any): dayjs.Dayjs | null {
		if (!value) {
			return null;
		}

		if (dayjs.isDayjs(value)) {
			return value;
		}

		// Handle native Date objects (from your booking object)
		if (value instanceof Date) {
			return dayjs(value);
		}

		// Handle string dates
		if (typeof value === 'string') {
			const parsed = dayjs(value);
			return parsed.isValid() ? parsed : null;
		}

		// Handle timestamps
		if (typeof value === 'number') {
			return dayjs(value);
		}

		// Try to parse anything else
		const parsed = dayjs(value);
		return parsed.isValid() ? parsed : null;
	}
}
